import { type ChangeEvent, type DragEvent, useRef, useState } from 'react'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

export interface TextFileFieldProps {
    /** The text, whether pasted or read from a file. */
    value: string
    /** Called with the new text and, when it came from a file, that file's name. */
    onChange: (value: string, fileName?: string) => void
    /** What the drop zone invites, e.g. "Drop a license file here". */
    dropLabel: string
    placeholder?: string
    /** The file picker's accept attribute; text files by default. */
    accept?: string
    disabled?: boolean
    minRows?: number
    maxRows?: number
    /** Reported when a dropped or selected file cannot be read, or is empty. */
    onFileError?: (message: string) => void
    'data-test-id'?: string
}

/**
 * A text document the user brings from another machine: a drop zone and file picker over a
 * multiline field for pasting. The offline license, activation request and activation response
 * flows all move small text files between an air-gapped appliance and a connected browser.
 */
export const TextFileField = ({
    value,
    onChange,
    dropLabel,
    placeholder,
    accept = '.txt,.json,text/plain,application/json',
    disabled = false,
    minRows = 3,
    maxRows = 6,
    onFileError,
    'data-test-id': testId,
}: TextFileFieldProps) => {
    const [fileName, setFileName] = useState<string>()
    const [dragging, setDragging] = useState(false)
    // Drag events fire for every child entered and left; only the outermost pair matters.
    const dragDepth = useRef(0)

    const loadFile = async (file: File) => {
        try {
            const text = (await file.text()).trim()
            if (!text) throw new Error(`${file.name} is empty`)
            setFileName(file.name)
            onChange(text, file.name)
        } catch (error) {
            onFileError?.(error instanceof Error ? error.message : `Could not read ${file.name}`)
        }
    }

    const selectFile = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        event.target.value = ''
        if (file) void loadFile(file)
    }

    const enter = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault()
        if (disabled) return
        dragDepth.current += 1
        setDragging(true)
    }

    const leave = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault()
        dragDepth.current = Math.max(0, dragDepth.current - 1)
        if (dragDepth.current === 0) setDragging(false)
    }

    const drop = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault()
        dragDepth.current = 0
        setDragging(false)
        if (disabled) return
        const file = event.dataTransfer.files[0]
        if (file) void loadFile(file)
    }

    return (
        <Box data-test-id={testId}>
            <Box
                onDragEnter={enter}
                onDragOver={(event) => {
                    event.preventDefault()
                    event.dataTransfer.dropEffect = disabled ? 'none' : 'copy'
                }}
                onDragLeave={leave}
                onDrop={drop}
                sx={{
                    mb: 2,
                    p: 3,
                    border: 2,
                    borderStyle: 'dashed',
                    borderColor: dragging ? 'primary.main' : 'divider',
                    borderRadius: 1,
                    bgcolor: dragging ? 'action.hover' : 'transparent',
                    textAlign: 'center',
                    opacity: disabled ? 0.6 : 1,
                }}
            >
                <UploadFileIcon color={dragging ? 'primary' : 'action'} sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="body2" sx={{ mb: 1 }}>
                    {dropLabel}
                </Typography>
                <Button component="label" variant="outlined" startIcon={<UploadFileIcon />} disabled={disabled}>
                    Select file
                    <input hidden type="file" accept={accept} onChange={selectFile} disabled={disabled} />
                </Button>
                {fileName && value ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        Loaded: {fileName}
                    </Typography>
                ) : null}
            </Box>
            <TextField
                fullWidth
                size="small"
                multiline
                minRows={minRows}
                maxRows={maxRows}
                value={value}
                disabled={disabled}
                onChange={(event) => {
                    setFileName(undefined)
                    onChange(event.target.value)
                }}
                placeholder={placeholder}
                slotProps={{ input: { sx: { fontFamily: 'monospace', fontSize: '0.8rem' } } }}
            />
        </Box>
    )
}
